/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae landing page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Link } from 'react-router-dom';

import { useHistory, useParams } from 'react-router';

import Typography from '@material-ui/core/Typography';

import { Theme, makeStyles } from '@material-ui/core/styles';


import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { Button, Grid, LinearProgress, Paper } from '@material-ui/core';
import { ALL_INSTITUTIONS_CODE, CollapsibleTable, InstitutionSelector, InstitutionsContext } from '@ngt/online-patient-management-ui';
import { RequestState } from '@ngt/request-utilities';
import { DateTime } from 'luxon';
import { Column } from 'react-table';
import * as Dtos from '../../api/Neon.Dtos';
import SaeBreadcrumbs from '../../components/Breadcrumbs/SaeBreadcrumbs';
import { useSaeById } from '../../store/modules/saereview/sae';
import { useSaes } from '../../store/modules/saereview/saes';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaePageParams {
    institutionCode?: string;
}

interface ISaePageProps {
}

interface ISaeButtonProps {
    patientInstitutionCode: string;
    patientStudyNumber: string;
    saeNumber: number;
    label: string;
    urlSuffix: string;
}


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    buttonSet: {
        textAlign: 'right'
    },
    button: {
        marginLeft: theme.spacing(1),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    },
    ml1: {
        marginLeft: theme.spacing(1),
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const saePermissions = [Dtos.Permission.SaeView, Dtos.Permission.SaeReview, Dtos.Permission.SaeAdminister, Dtos.Permission.MedicalReviewPerform];
const medicalReviewPermissions = [Dtos.Permission.MedicalReviewPerform];

const useSaeColumns = (saes: Array<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>> | null,
    institutions: Dtos.IInstitution[] | null,
    investigatorColumn: boolean,
    medicalReviewColumn: boolean,
    canViewSae: boolean,
    canReviewSae: boolean,
    canAdministerSae: boolean,
    canMedicallyReviewSae: boolean) => {

    const classes = useStyles();

    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>>> = [
            {
                Header: 'Reg No',
                accessor: sae => sae.patient.studyNumber,
                minWidth: 110,
                maxWidth: 110
            },
            {
                Header: 'SAE No',
                accessor: sae => sae.form.saeNumber,
                minWidth: 80,
                maxWidth: 80
            },
            {
                Header: 'Onset Date',
                accessor: sae => sae.form.dateOfSAE,
                Cell: (props: any) => props.cell.value ? DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy') : '',
                minWidth: 100,
                maxWidth: 100
            },
            {
                Header: 'Main Event',
                accessor: sae => sae.form.ctcaeTerm ?? sae.form.mainEvent,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Site',
                accessor: sae => institutions?.find(i => i.id === (sae.patient.institutionId))?.name,
                minWidth: 175,
                maxWidth: 175
            },
            {
                Header: 'Status',
                accessor: sae => sae.form.eventStatus,
                Cell: (props: any) => {
                    return (
                        <>
                            {props.row.original.form.followUpNumber > 0 ? 'Follow-up no.' + props.row.original.form.followUpNumber : 'Initial'} {!!props.row.original.form.eventStatus ? '(' + props.row.original.form.eventStatus + ')' : ''}
                        </>
                    );
                },
                minWidth: 100,
                maxWidth: 100
            },
            {
                Header: 'View',
                accessor: sae => sae.form.id,
                Cell: (props: any) => {
                    return (
                        <>
                            <SaeButton
                                patientInstitutionCode={props.row.original.patient.institutionCode}
                                patientStudyNumber={props.row.original.patient.studyNumber}
                                saeNumber={props.row.original.form.saeNumber}
                                label={props.row.original.status === Dtos.SaeStatus.AwaitingForReview && canReviewSae ? 'Review' : 'View'}
                                urlSuffix={props.row.original.status === Dtos.SaeStatus.AwaitingForReview && canReviewSae  ? 'review' : ''}
                            />
                        </>
                    );
                },
                minWidth: 30,
                maxWidth: 30
            },
        ];

        if (investigatorColumn && canAdministerSae) {
            cols.push({
                Header: 'Investigator',
                accessor: sae => sae.form.id,
                Cell: (props: any) => {
                    return SaeButton({
                        patientInstitutionCode: props.row.original.patient.institutionCode,
                        patientStudyNumber: props.row.original.patient.studyNumber,
                        saeNumber: props.row.original.form.saeNumber,
                        label: 'Notify',
                        urlSuffix: 'notify/investigators'
                    });
                },
                minWidth: 60,
                maxWidth: 60
            });
        }

        if (medicalReviewColumn && canMedicallyReviewSae) {
            cols.push({
                Header: 'Medical Review',
                accessor: sae => sae.form.id,
                Cell: (props: any) => {
                    const sae: Dtos.Sae<Dtos.SaeForm, Dtos.Patient> = props.row.original;
                    const saeMedicalReviewStatus = sae.actionHistory
                        .find(a => a.actionType === Dtos.SaeWebActionTypeEnum.NotifiedMedicalReviewers || a.actionType === Dtos.SaeWebActionTypeEnum.SubmittedMedicalReview);

                    return (
                        <>
                            {
                                saeMedicalReviewStatus?.actionType === Dtos.SaeWebActionTypeEnum.NotifiedMedicalReviewers && <SaeButton
                                    patientInstitutionCode={props.row.original.patient.institutionCode}
                                    patientStudyNumber={props.row.original.patient.studyNumber}
                                    saeNumber={props.row.original.form.saeNumber}
                                    label="Review"
                                    urlSuffix="mreview"
                                />
                            }

                            {
                                !!canAdministerSae && <SaeButton
                                    patientInstitutionCode={props.row.original.patient.institutionCode}
                                    patientStudyNumber={props.row.original.patient.studyNumber}
                                    saeNumber={props.row.original.form.saeNumber}
                                    label="Notify"
                                    urlSuffix="notify/mreviewer"
                                />
                            }

                            {
                                saeMedicalReviewStatus?.actionType === Dtos.SaeWebActionTypeEnum.NotifiedMedicalReviewers &&
                                <span className={classes.ml1}>Pending</span>
                            }

                            {
                                saeMedicalReviewStatus?.actionType === Dtos.SaeWebActionTypeEnum.SubmittedMedicalReview &&
                                <span className={classes.ml1}>Reviewed</span>
                            }
                        </>
                    );
                },
                minWidth: 240,
                maxWidth: 240
            });
        }

        return cols;
    }, [canAdministerSae, canMedicallyReviewSae, canReviewSae, classes.ml1, institutions, investigatorColumn]);

    return columns;
};

const useInvalidSaeColumns = (institutions: Dtos.IInstitution[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>>> = [
            {
                Header: 'Reg No',
                accessor: sae => sae.patient.studyNumber,
                minWidth: 110,
                maxWidth: 110
            },
            {
                Header: 'SAE No',
                accessor: sae => sae.form.saeNumber,
                minWidth: 80,
                maxWidth: 80
            },
            {
                Header: 'Site',
                accessor: sae => institutions?.find(i => i.id === (sae.patient.institutionId))?.name,
                minWidth: 175,
                maxWidth: 175
            },
            {
                Header: 'View',
                accessor: sae => sae.form.id,
                Cell: (props: any) => {
                    return (
                        <>
                            <SaeButton
                                patientInstitutionCode={props.row.original.patient.institutionCode}
                                patientStudyNumber={props.row.original.patient.studyNumber}
                                saeNumber={props.row.original.form.saeNumber}
                                label={'View'}
                                urlSuffix={''}
                            />
                        </>
                    );
                },
                minWidth: 30,
                maxWidth: 30
            },
        ];

        return cols;
    }, [institutions]);

    return columns;
};

const SaeButton: React.FunctionComponent<ISaeButtonProps> = ({
    patientInstitutionCode,
    patientStudyNumber,
    saeNumber,
    label,
    urlSuffix
}) => {
    const classes = useStyles();

    const [, , , ] = useSaeById(patientStudyNumber, saeNumber);

    let saeLink = `/sae/${patientInstitutionCode}/${patientStudyNumber}/${saeNumber}`

    if (urlSuffix !== '') {
        saeLink += `/${urlSuffix}`
    }

    return <>
        <Button
            variant="contained"
            color="primary"
            component={Link}
            className={classes.button}
            to={saeLink}
            size="small"
        >
            {label}
        </Button>
    </>
};

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */


const SaePage: React.FunctionComponent<ISaePageProps> = () => {
    const classes = useStyles();

    const history = useHistory();

    const params = useParams<ISaePageParams>();

    const [[canViewSae, canReviewSae, canAdministerSae, canMedicallyReviewSae], saePermissionLoadState] = useBctApiPermissions(saePermissions);

    const [[canMedicalReview], medicalReviewPermissionLoadState] = useBctApiPermissions(medicalReviewPermissions);

    const [institutions, institutionsLoadState, ] = React.useContext(InstitutionsContext);

    const institutionsToUse = React.useMemo(() => {
        return institutions?.filter(i => (i as Dtos.Institution).activatedDate != null) ?? []
    }, [institutions]);

    const [saes, saesLoadState, saesActions] = useSaes(
        params.institutionCode === ALL_INSTITUTIONS_CODE ? undefined : params.institutionCode
    );

    React.useEffect(() => {
        if (saePermissionLoadState.state !== RequestState.Pending &&
            saePermissionLoadState.state !== RequestState.None &&
            medicalReviewPermissionLoadState.state !== RequestState.Pending &&
            medicalReviewPermissionLoadState.state !== RequestState.None) {
            if (!canViewSae) {
                history.replace(`/error/403`);
            }
            else if (!canAdministerSae && !canMedicalReview &&
                institutions && institutions.length > 0) {
                if (params.institutionCode &&
                    params.institutionCode !== ALL_INSTITUTIONS_CODE) {
                    history.replace(`/sae/${params.institutionCode}`);
                }
                else {
                    history.replace(`/sae/${institutions[0].code}`);
                }
            }
        }
    }, [institutions, canViewSae, canAdministerSae, saePermissionLoadState, history, params.institutionCode, canMedicalReview, medicalReviewPermissionLoadState]);

    const institutionsLoading = institutionsLoadState.state === RequestState.None || institutionsLoadState.state === RequestState.Pending;

    const newSaes = React.useMemo(() => {
        if (!saes) {
            return null;
        }

        return saes.filter(sae => sae.status === Dtos.SaeStatus.Added || sae.status === Dtos.SaeStatus.New);
    }, [saes]);

    const awaitingSaes = React.useMemo(() => {
        if (!saes) {
            return null;
        }

        return saes.filter(sae => sae.status === Dtos.SaeStatus.AwaitingForReview);
    }, [saes]);

    const approvedSaes = React.useMemo(() => {
        if (!saes) {
            return null;
        }

        return saes.filter(sae => sae.status === Dtos.SaeStatus.Approved);
    }, [saes]);

    const rejectedSaes = React.useMemo(() => {
        if (!saes) {
            return null;
        }

        return saes.filter(sae => sae.status === Dtos.SaeStatus.Rejected);
    }, [saes]);

    const incorrectSaes = React.useMemo(() => {
        if (!saes) {
            return null;
        }

        return saes.filter(sae => sae.status === Dtos.SaeStatus.Incorrect);
    }, [saes]);

    const invalidSaes = React.useMemo(() => {
        if (!saes) {
            return null;
        }

        return saes.filter(sae => sae.status === Dtos.SaeStatus.Invalid);
    }, [saes]);

    React.useEffect(() => {
        saesActions.load();

        return () => {
            saesActions.clear();
        }
    }, [saesActions]);

    const saeColumnsWithInvestigatorColumn = useSaeColumns(saes, institutions, true, true, canViewSae ?? false, canReviewSae ?? false, canAdministerSae ?? false, canMedicallyReviewSae ?? false);
    const saeColumnsWithoutInvestigatorColumn = useSaeColumns(saes, institutions, false, true, canViewSae ?? false, canReviewSae ?? false, canAdministerSae ?? false, canMedicallyReviewSae ?? false);
    const invalidSaeColumns = useInvalidSaeColumns(institutions);

    return (
        <>

        {
            !saes && saesLoadState.state === RequestState.Pending &&
            <LinearProgress />
        }
        <PageLayout
            breadcrumbs={
                <SaeBreadcrumbs
                    institutionName={institutions?.find(i => i.code === params.institutionCode)?.name}
                />
            }
            heading="Serious Adverse Events"
        >
            <Paper
                className={classes.container}
            >

                <Grid container alignItems="flex-end" spacing={2}>
                    <Grid item xs={6}>
                        <InstitutionSelector
                            allowAllInstitutions={canAdministerSae || canMedicalReview}
                            baseRoute="/sae"
                            institutionOnlyRoute="/sae/:institutionCode"
                            institutionCode={params.institutionCode}
                            institutions={institutionsToUse}
                            loading={institutionsLoading}
                            showCountry={false}
                            showTreatment={false}
                            countryOnlyRoute="/sae"
                            countryAndInstitutionOnlyRoute="/sae/:institutionCode"
                            />
                    </Grid>
                </Grid>

                {
                    !!newSaes &&
                    !!canAdministerSae &&
                    <CollapsibleTable
                        title="New SAE"
                        loadState={saesLoadState}
                        data={newSaes}
                        columns={saeColumnsWithInvestigatorColumn}
                    />
                }

                {
                    !!awaitingSaes &&
                    <CollapsibleTable
                        title="Awaiting review SAE"
                        loadState={saesLoadState}
                        data={awaitingSaes}
                        columns={saeColumnsWithInvestigatorColumn}
                    />
                }

                {
                    !!approvedSaes &&
                    <CollapsibleTable
                        title="Approved SAE"
                        loadState={saesLoadState}
                        data={approvedSaes}
                        columns={saeColumnsWithoutInvestigatorColumn}
                    />
                }

                {
                    !!rejectedSaes &&
                    <CollapsibleTable
                        title="Rejected SAE"
                        loadState={saesLoadState}
                        data={rejectedSaes}
                        columns={saeColumnsWithoutInvestigatorColumn}
                    />
                }

                {
                    !!incorrectSaes &&
                    !!canAdministerSae &&
                    <CollapsibleTable
                        title="Incorrectly Identified SAE"
                        loadState={saesLoadState}
                        data={incorrectSaes}
                        columns={saeColumnsWithoutInvestigatorColumn}
                    />
                }

                {
                    !!invalidSaes &&
                    <CollapsibleTable
                        title="Invalid SAE"
                        loadState={saesLoadState}
                        data={invalidSaes}
                        columns={invalidSaeColumns}
                    />
                }
            </Paper>
        </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default SaePage;